<template>
  <nav
    class="jps-pagination"
    role="navigation"
    v-if="ss.state.result.hit > 0"
    :aria-label="ls(dapLangGlobal.pagination)"
  >
    <button
      class="pagination-button is-prev"
      @click="goto(page - 1)"
      :disabled="page <= 1"
      :aria-label="ls(dapLangGlobal.prev)"
    >
      <BaseIcon name="mdi/arrow-left" />
    </button>

    <button
      class="pagination-button is-next"
      @click="goto(page + 1)"
      :disabled="page >= maxPage"
      :aria-label="ls(dapLangGlobal.next)"
    >
      <BaseIcon name="mdi/arrow-right" />
    </button>

    <div
      class="jps-pagination-list"
    >
      <button
        v-if="show1"
        class="pagination-button"
        @click="goto(1)"
        aria-label="Goto page 1"
      >
        1
      </button>

      <button
        v-for="p in pageArray"
        :key="p"
        class="pagination-button"
        :class="{ 'is-current': p === page }"
        :aria-label="'page ' + p"
        @click="goto(p)"
      >
        {{ p }}
      </button>
      <template v-if="show2">
        <span
          v-show="pageArray[pageArray.length - 1] < maxPage - 1"
          class="pagination-dots"
          >&hellip;</span>
        <button
          class="pagination-button"
          @click="goto(maxPage)"
          :aria-label="'page ' + maxPage"
        >
          {{ maxPage }}
        </button>
      </template>
    </div>
  </nav>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
const { ls } = useI18n()

interface Props {
  size?: number
  autoscroll?: boolean
  ss: SearchStore<any>
}
const props = withDefaults(defineProps<Props>(), {
  size: 2,
  autoscroll: false,
})

const goto = page => {
  if (page < 0) page = 0
  if (page > maxPage.value) page = maxPage.value
  if (props.autoscroll) props.ss.setScrollAfterSearch()
  props.ss.setFrom(props.ss.state.currentQuery?.size * (page - 1))
}

const page = computed(() => {
  return (
    Math.floor(
      props.ss.state.currentQuery?.from / props.ss.state.currentQuery?.size,
    ) + 1
  )
})

const show1 = computed(() => {
  return page.value - props.size > 1
})

const show2 = computed(() => {
  return page.value < maxPage.value - props.size && maxPage.value < 20
})

const pageArray = computed(() => {
  let arr = []
  for (let i = page.value - props.size; i < page.value + props.size + 1; i++) {
    if (i > 0 && i <= maxPage.value) arr.push(i)
  }
  return arr
})

const maxPage = computed(() => {
  return Math.ceil(
    props.ss.state.result?.hit / props.ss.state.currentQuery.size,
  )
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-pagination {
  justify-content: space-between;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}

.pagination-button {
  cursor: pointer;
  display: flex;
  height: calc(0.25rem * 8);
  width: calc(0.25rem * 8);
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  background-color: $jps-gray-100;
  color: $jps-key-blue;
  @include ease();

  &.is-prev {
    order: 1;
  }

  &.is-next {
    order: 3;
  }

  &.is-current {
    pointer-events: none;
    background-color: $jps-key-blue !important;
    color: $white !important;
  }

  &:hover {
    text-decoration: underline;
    background-color: $jps-gray-200;
  }

  &:focus, &:active {
    border: 1px solid $jps-key-blue-hover;
  }

  &:disabled {
    pointer-events: none;
    background-color: $jps-gray-200;
    color: $jps-gray-500;
  }
}

.is-centered {
  .jps-pagination-list {
    justify-content: center;
    align-items: center;
    order: 2;
  }

  .pagination-button {
    &.is-prev {
      order: 1;
    }

    &.is-next {
      order: 3;
    }
  }
}

.is-right {
  .jps-pagination-list {
    justify-content: flex-end;
    order: 3;
  }
  .pagination-button {
    &.is-prev {
      order: 1;
    }

    &.is-next {
      order: 2;
    }
  }
}

// .jps-pagination-list {
//   max-width: 18rem;
//   min-width: 9rem;
//   display: flex;
//   flex-grow: 1;
//   flex-shrink: 1;
//   justify-content: flex-start;
//   order: 1;
// }

.jps-pagination-ellipsis {
  color: $jps-gray-500;
  pointer-events: none;
}

.jps-pagination-list {
  order: 2;
  display: flex;
  min-width: 9rem;
  max-width: 18rem;
  flex-shrink: 1;
  flex-grow: 1;
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 700;
  gap: 0.25rem;

  @include is-tablet {
    font-size: 1rem;
    line-height: 1.5rem;
  }
}

.pagination-dots {
  pointer-events: none;
  color: $jps-gray-500;
}
</style>
